"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Calendar, Check, Clock, Info, Star, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  post_type: "product",
  per_page: 100,
  status: "publish"
};
const wp_query = new WP_Query(wp_query_params);
function ServiceCard({
  product,
  onClick,
  index
}: {
  product: any;
  onClick: () => void;
  index: number;
}) {
  return <div data-index={index} className="group relative flex flex-col h-[600px] w-full cursor-pointer overflow-hidden rounded-lg border border-border bg-card transition-all duration-300 hover:shadow-lg hover:-translate-y-1" onClick={onClick}>
      <div className="relative h-2/3 w-full overflow-hidden">
        <img src={product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555315/img/studio_service_placeholder.png"} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <div className="absolute top-4 right-4">
          <Badge className="bg-primary text-primary-foreground font-bold rounded-lg px-3 py-1 shadow-sm">
            SAR {product.price || "500"}
          </Badge>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="mb-2 text-xl font-bold tracking-tight text-foreground font-serif line-clamp-1">
          {product.title}
        </h3>
        <div className="line-clamp-2 text-sm text-muted-foreground leading-relaxed mb-4" dangerouslySetInnerHTML={{
        __html: product.short_description || "Professional studio service tailored to your needs."
      }} />
        <div className="mt-auto flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-widest">
          Explore Service <ChevronRight className="h-3 w-3" />
        </div>
      </div>
    </div>;
}
function ServicesGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="h-[600px] w-full animate-pulse rounded-lg bg-muted/20 border border-border" />)}
      </div>;
  }
  if (error) {
    return <div className="flex h-[400px] w-full flex-col items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
        <Info className="mb-4 h-12 w-12 text-destructive" />
        <h3 className="text-xl font-bold text-destructive font-serif">Service Retrieval Failed</h3>
        <p className="mt-2 max-w-xs text-muted-foreground">
          We encountered an industrial error while fetching our services. Please refresh or contact support.
        </p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex h-[400px] w-full items-center justify-center rounded-lg border border-dashed border-border bg-muted/5 p-8 text-center">
        <p className="text-muted-foreground font-medium">No studio services are currently listed.</p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {products.map((product, index) => <ServiceCard key={product.id} index={index} product={product} onClick={() => setSelectedProduct(product)} />)}
      </div>

      <Dialog open={!!selectedProduct} onOpenChange={open => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-4xl overflow-hidden p-0 gap-0 bg-background border-border rounded-lg shadow-2xl">
          {selectedProduct && <div className="flex flex-col lg:flex-row">
              <div className="relative h-64 w-full lg:h-auto lg:w-1/2 shrink-0">
                <img src={selectedProduct.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555315/img/studio_service_placeholder.png"} alt={selectedProduct.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent lg:bg-gradient-to-r" />
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <Badge className="mb-4 bg-secondary text-secondary-foreground rounded-lg px-3 py-1">
                    Premium Studio Service
                  </Badge>
                  <h3 className="text-3xl font-bold font-serif leading-tight">
                    {selectedProduct.title}
                  </h3>
                </div>
              </div>

              <div className="flex flex-1 flex-col p-8 lg:p-12">
                <DialogHeader className="mb-8 text-left">
                  <div className="flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-widest mb-2">
                    <Star className="h-3.5 w-3.5 fill-primary" />
                    <span>Top Rated Expertise</span>
                  </div>
                  <DialogTitle className="text-2xl font-bold text-foreground font-serif">
                    Service Specifications
                  </DialogTitle>
                </DialogHeader>

                <div className="flex-1 space-y-8">
                  <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{
                __html: selectedProduct.short_description || selectedProduct.long_description || "<p>Experience our premium studio services with state-of-the-art equipment and professional guidance.</p>"
              }} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Check className="h-4 w-4" />
                      </div>
                      <span>Pro Post-Processing</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Clock className="h-4 w-4" />
                      </div>
                      <span>Flexible Duration</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <SafeHydrate><Calendar className="h-4 w-4" /></SafeHydrate>
                      </div>
                      <span>Instant Booking</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Star className="h-4 w-4" />
                      </div>
                      <span>High-Res Delivery</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-border pt-8">
                    <div>
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Starting From</p>
                      <p className="text-3xl font-bold text-primary font-serif">SAR {selectedProduct.price || "500"}</p>
                    </div>
                    <AddToCartButton productId={selectedProduct.id} variant="default" className="w-auto" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-10 text-lg font-bold shadow-lg rounded-lg transition-all" text="Book Session" showQuantity={false} />
                  </div>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </>;
}
export default function StudioServicesPreview() {
  return <section id="studio-services-preview" className="bg-background text-foreground py-40 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px w-12 bg-primary" />
              <span className="text-sm font-bold uppercase tracking-[0.3em] text-primary">
                The Precision Press
              </span>
            </div>
            <h2 className="text-5xl font-bold tracking-tighter text-foreground sm:text-6xl md:text-7xl font-serif mb-8">
              Studio <span className="text-primary">Services</span>
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl">
              Bridging the gap between cinematic vision and industrial excellence. Our Riyadh-based studio delivers high-end photography and videography with the structured reliability of a modern printing house.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div className="p-8 border border-border bg-card rounded-lg shadow-sm max-w-xs">
              <p className="text-sm font-bold text-secondary uppercase tracking-widest mb-2">Equipment Focus</p>
              <p className="text-sm text-muted-foreground">Utilizing the Canon Pro 6600 for unmatched large-scale precision in every print.</p>
            </div>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        post_type: "product",
        per_page: 100,
        status: ["publish"]
      }} postType="product">
          <ServicesGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}